import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../_shared/hooks';
import { fetchTemplates, deleteTemplate, renameTemplate, updateTemplateRows, fetchWaterfallConfigs, updateWaterfallConfigRows } from '../api-client';
import type { WaterfallConfigRow } from '../api-client';
import type { ScorecardTemplate } from '../components/types';
import type { WaterfallRow } from '../components/waterfallTypes';

export function useLauncherTemplates(campaignType?: string) {
  const { apiFetch } = useApi();

  const query = useQuery<ScorecardTemplate[]>({
    queryKey: ['launcher', 'templates', campaignType ?? null],
    queryFn: () => fetchTemplates(apiFetch, campaignType),
    staleTime: Infinity,
  });

  return {
    templates: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  } as const;
}

export function useDeleteTemplate() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTemplate(apiFetch, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'templates'] });
    },
  });
}

export function useRenameTemplate() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameTemplate(apiFetch, id, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'templates'] });
    },
  });
}

export function useUpdateTemplateRows() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rows }: { id: string; rows: WaterfallRow[] }) => updateTemplateRows(apiFetch, id, rows),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'templates'] });
    },
  });
}

export function useWaterfallConfigs() {
  const { apiFetch } = useApi();
  const query = useQuery<WaterfallConfigRow[]>({
    queryKey: ['launcher', 'waterfall-configs'],
    queryFn: () => fetchWaterfallConfigs(apiFetch),
    staleTime: Infinity,
  });
  return {
    configs: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
  } as const;
}

export function useUpdateWaterfallConfigRows() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, rows }: { campaignId: string; rows: WaterfallRow[] }) =>
      updateWaterfallConfigRows(apiFetch, campaignId, rows),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['launcher', 'waterfall-configs'] });
    },
  });
}
